/**
 * Dropdown, pure half.
 *
 * Moved verbatim from components/FlowBuilder/flow-components/components/DropdownComponent.js. Runs unchanged in the
 * browser and, via the flow-core bundle, inside GraalJS on the backend, so it
 * must never reach for React, the DOM, Node APIs, or the network.
 */

import { labelLengthWarnings } from '../labelLength';
import { reservedFieldNameErrors } from '../fieldName';
import { DATA_SOURCE_MODE, DATA_SOURCE_REF, defaultBindingProps, isDynamic, dataSourceForWhatsApp, readLoadedDataSource, validateBinding } from '../dataSourceBinding';

export const DropdownCore = {
  type: 'Dropdown',
  name: 'Dropdown',
  icon: '📋',
  description: 'Pick one option from a scrollable list.',
  category: 'input',
  maxPerScreen: null,
  getDefaultProperties() {
    return {
      name: 'dropdown',
      label: 'Select option',
      required: false,
      'data-source': [
        { id: 'option1', title: 'Option 1' },
        { id: 'option2', title: 'Option 2' },
      ],
      ...defaultBindingProps(),
      'init-value': '',
      enabled: true,
      visible: true,
    };
  },
  getPropertySchema() {
    return {
      name: {
        type: 'string',
        label: 'Field Name',
        required: true,
      },
      label: {
        type: 'string',
        label: 'Label',
        required: true,
      },
      required: {
        type: 'boolean',
        label: 'Required',
        required: false,
      },
      'data-source': {
        type: 'array',
        label: 'Options',
        required: true,
        itemSchema: { id: 'string', title: 'string' },
      },
      'init-value': {
        type: 'string',
        label: 'Initial Value',
        required: false,
      },
      enabled: {
        type: 'boolean',
        label: 'Enabled',
        required: false,
      },
      visible: {
        type: 'boolean',
        label: 'Visible',
        required: false,
      },
    };
  },
  validateProperties(properties) {
    var errors = [];
    var warnings = [];

    if (!properties.name || properties.name.trim().length === 0) {
      errors.push('Field name is required');
    }

    if (properties.name && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(properties.name)) {
      errors.push('Field name must start with letter or underscore');
    }

    errors.push(...reservedFieldNameErrors(properties.name));

    if (!properties.label || properties.label.trim().length === 0) {
      errors.push('Label is required');
    }

    warnings.push(...labelLengthWarnings('Dropdown', properties.label));

    if (properties.label && properties.label.length > 80) {
      errors.push('Label must be 80 characters or less');
    }

    if (isDynamic(properties)) {
      var binding = validateBinding(properties);
      errors.push(...binding.errors);
      warnings.push(...binding.warnings);
      return { errors, warnings };
    }

    if (!properties['data-source'] || properties['data-source'].length === 0) {
      errors.push('At least one option is required');
    }

    if (properties['data-source']) {
      var ids = new Set();
      properties['data-source'].forEach((option, index) => {
        if (!option.id) {
          errors.push(`Option ${index + 1} is missing an ID`);
        } else if (ids.has(option.id)) {
          errors.push(`Duplicate option ID: ${option.id}`);
        } else {
          ids.add(option.id);
        }

        if (!option.title) {
          errors.push(`Option ${index + 1} is missing a title`);
        }
      });

      if (properties['data-source'].length > 200) {
        errors.push('Maximum 200 options allowed');
      }
    }

    if (properties['init-value'] && properties['data-source']) {
      var validIds = properties['data-source'].map((opt) => opt.id);
      if (!validIds.includes(properties['init-value'])) {
        warnings.push('Initial value does not match any option ID');
      }
    }

    return { errors, warnings };
  },
  transformToWhatsApp(properties) {
    var result = {
      type: 'Dropdown',
      name: properties.name,
      label: properties.label,
      required: properties.required || false,
      'data-source': dataSourceForWhatsApp(properties, properties['data-source'] || []),
    };

    if (properties['init-value'] && properties['init-value'].trim()) {
      result['init-value'] = properties['init-value'];
    }

    if (properties.enabled === false) {
      result.enabled = false;
    }

    if (properties.visible === false) {
      result.visible = false;
    }

    return result;
  },
  transformFromWhatsApp(whatsappComponent) {
    var loaded = readLoadedDataSource(whatsappComponent['data-source']);
    return {
      name: whatsappComponent.name,
      label: whatsappComponent.label,
      required: whatsappComponent.required || false,
      'data-source': loaded.array,
      [DATA_SOURCE_MODE]: loaded.mode,
      [DATA_SOURCE_REF]: loaded.ref,
      'init-value': whatsappComponent['init-value'] || '',
      enabled: whatsappComponent.enabled !== false,
      visible: whatsappComponent.visible !== false,
    };
  },
};
